import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, MoreHorizontal, MoreVerticalIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export interface Column<T> {
  key: string;
  header: React.ReactNode;
  accessor?: keyof T | ((row: T) => React.ReactNode);
  sortable?: boolean;
  className?: string;
  headerClassName?: string;
  render?: (value: any, row: T, index: number) => React.ReactNode;
}

/**
 * One row action — a single item in the standard actions dropdown. Propagation
 * is stopped before `onClick` fires, so clicking a menu item never triggers the
 * row's `onRowClick`.
 */
export interface RowAction {
  label: React.ReactNode;
  icon?: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  /** Render in destructive (red) styling — use for Delete / Remove. */
  destructive?: boolean;
  /** Insert a separator above this item (ignored for the first visible item). */
  separatorBefore?: boolean;
  /** Omit this item entirely — handy for conditional actions. */
  hidden?: boolean;
}

/** The standard right-aligned 3-dot (`MoreHorizontal`) actions dropdown cell. */
function RowActionsCell({ actions }: { actions: RowAction[] }) {
  const visible = actions.filter((a) => !a.hidden);
  if (visible.length === 0) return null;
  return (
    <div
      className="flex justify-end"
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" aria-label="Row actions">
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-44">
          {visible.map((action, index) => (
            <React.Fragment key={index}>
              {action.separatorBefore && index > 0 && <DropdownMenuSeparator />}
              <DropdownMenuItem
                className={cn('gap-2', action.destructive && 'text-red-600 focus:text-red-600')}
                disabled={action.disabled}
                onClick={(e) => {
                  e.stopPropagation();
                  action.onClick?.();
                }}
              >
                {action.icon}
                {action.label}
              </DropdownMenuItem>
            </React.Fragment>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

/**
 * Build the standard right-aligned "Actions" column wired to the shared actions
 * dropdown. Prefer the `rowActions` prop on DataTable for the common case; use
 * this helper when you assemble the columns array yourself (e.g. AdvancedTable)
 * or need the actions column somewhere other than last.
 */
export function createActionsColumn<T>(
  getActions: (row: T, index: number) => RowAction[],
  options?: { header?: React.ReactNode; key?: string; className?: string; headerClassName?: string },
): Column<T> {
  return {
    key: options?.key ?? 'actions',
    header: options?.header ?? 'Actions',
    headerClassName: cn('text-right', options?.headerClassName),
    className: cn('text-right', options?.className),
    render: (_value, row, index) => <RowActionsCell actions={getActions(row, index)} />,
  };
}

export interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  pageSize?: number;
  currentPage?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  totalItems?: number;
  loading?: boolean;
  emptyMessage?: React.ReactNode;
  className?: string;
  onRowClick?: (row: T, index: number) => void;
  selectable?: boolean;
  onSelectionChange?: (selectedRows: T[]) => void;
  /**
   * Optional per-row gate for selectability. When it returns false for a row,
   * that row's checkbox renders disabled, the row is excluded from select-all,
   * and it never appears in `onSelectionChange`. Default (no prop) = every row
   * selectable, so existing callers are unaffected.
   */
  isRowSelectable?: (row: T, index: number) => boolean;
  showPagination?: boolean;
  pageSizeOptions?: number[];
  innerScroll?: boolean;
  virtualize?: boolean;
  virtualRowHeight?: number;
  selectionVersion?: number;
  /**
   * When provided, DataTable reconciles its internal index-based selection with
   * these IDs every time `data` changes. Rows whose `id` is in `selectedIds`
   * render as checked even after data refetches (search, filter, pagination).
   * Requires each row in `data` to have an `id: string` property.
   */
  selectedIds?: string[];
  /**
   * Controlled, id-based selection (cross-page). When provided, each row's
   * checked state is computed by id via `getRowId`, so selections persist
   * across pages/refetches (powers the contacts "Select all N matching").
   * Requires `getRowId`. Distinct from `selectedIds` (index-sync) above.
   */
  selectedRowIds?: Set<string> | string[];
  getRowId?: (row: T, index: number) => string;
  /**
   * When provided, DataTable appends the standard right-aligned actions column
   * (a 3-dot `MoreHorizontal` dropdown) built from the returned action list —
   * the caller doesn't define an actions column or import anything. Ignored when
   * `columns` already contains a column with key `'actions'` (a custom actions
   * column wins), so existing callers are unaffected.
   */
  rowActions?: (row: T, index: number) => RowAction[];
}

function DataTable<T extends Record<string, any>>({
  columns: inputColumns,
  data,
  pageSize: controlledPageSize,
  currentPage: controlledPage,
  onPageChange,
  onPageSizeChange,
  totalItems,
  loading,
  emptyMessage = 'No data available',
  className,
  onRowClick,
  selectable = false,
  onSelectionChange,
  isRowSelectable,
  showPagination = true,
  pageSizeOptions = [10, 20], // Default limited to 10 and 20
  innerScroll = true,
  virtualize = false,
  virtualRowHeight = 52,
  selectionVersion = 0,
  selectedIds,
  selectedRowIds,
  getRowId,
  rowActions,
}: DataTableProps<T>) {
  // When `rowActions` is provided, append the standard actions column — unless
  // the caller already defined one (key === 'actions'), so custom actions win.
  const columns = useMemo<Column<T>[]>(() => {
    if (!rowActions) return inputColumns;
    if (inputColumns.some((c) => c.key === 'actions')) return inputColumns;
    return [...inputColumns, createActionsColumn<T>(rowActions)];
  }, [inputColumns, rowActions]);

  const [internalPage, setInternalPage] = useState(1);
  const [internalPageSize, setInternalPageSize] = useState(Math.max(10, controlledPageSize ?? 10));
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const prevSelectionVersion = useRef(selectionVersion);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  // Controlled, id-based selection mode (see prop docs). When on, the parent
  // owns the selection and the table only renders + reports the page view.
  const isControlledSelection = selectedRowIds !== undefined;
  const selectedIdSet = useMemo<Set<string>>(
    () => (selectedRowIds === undefined
      ? new Set<string>()
      : selectedRowIds instanceof Set
        ? selectedRowIds
        : new Set(selectedRowIds)),
    [selectedRowIds]
  );

  useEffect(() => {
    // In controlled mode the parent owns selection — never auto-clear it here.
    if (isControlledSelection) return;
    if (prevSelectionVersion.current !== selectionVersion) {
      prevSelectionVersion.current = selectionVersion;
      setSelectedRows(new Set());
    }
  }, [selectionVersion]);

  // Reconcile external selectedIds with internal index-based selection when
  // data changes (search/filter/pagination). This ensures previously-selected
  // rows that reappear in a new data set show as checked.
  useEffect(() => {
    if (!selectedIds || !selectable) return;
    const idSet = new Set(selectedIds);
    const newSelected = new Set<number>();
    data.forEach((row, idx) => {
      if ((row as any).id && idSet.has((row as any).id)) {
        newSelected.add(idx);
      }
    });
    setSelectedRows(newSelected);
    // Intentionally NOT calling onSelectionChange here — the parent already
    // owns the canonical selection; this effect just syncs the visual checkmarks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, selectedIds, selectable]);

  const virtualScrollRef = useRef<HTMLDivElement>(null);

  // Use controlled or internal page state
  const isControlled = controlledPage !== undefined;
  const currentPage = isControlled ? controlledPage : internalPage;

  // Use controlled or internal page size (minimum is 10)
  const isPageSizeControlled = controlledPageSize !== undefined;
  const pageSize = isPageSizeControlled ? controlledPageSize : Math.max(10, internalPageSize);

  // Derived loading state: if loading prop is not explicitly false, show skeletons if data is empty
  const effectivelyLoading = loading === true || (loading !== false && data.length === 0);

  const handlePageSizeChange = (newPageSize: number) => {
    // Ensure minimum page size is 10
    const validPageSize = Math.max(10, newPageSize);

    // Clear selected rows when page size changes (indices will be different).
    // Skipped in controlled mode: the parent owns an id-based selection that
    // survives page-size changes (page-local indices are irrelevant there).
    if (!isControlledSelection) {
      setSelectedRows(new Set());
      if (onSelectionChange) {
        onSelectionChange([]);
      }
    }

    if (!isPageSizeControlled) {
      setInternalPageSize(validPageSize);
    }

    // Reset to first page when page size changes
    if (!isControlled) {
      setInternalPage(1);
    }

    onPageSizeChange?.(validPageSize);
  };
  const handlePageChange = (page: number) => {
    if (!isControlled) {
      setInternalPage(page);
    }
    onPageChange?.(page);
    // Scroll to top of table so users can see the new rows
    tableContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Total count and whether we should show pagination
  // Always show pagination by default unless explicitly set to false
  const totalCount = totalItems !== undefined ? totalItems : data.length;
  const effectiveShowPagination = showPagination !== false;

  // Calculate total pages (ensure at least 1 page even with 0 items)
  const totalPages = useMemo(() => {
    const count = totalItems !== undefined ? totalItems : data.length;
    return Math.max(1, Math.ceil(count / pageSize));
  }, [data.length, pageSize, totalItems]);

  // Get paginated data
  const paginatedData = useMemo(() => {
    if (!effectiveShowPagination) return data;

    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;

    // If totalItems is provided, we assume data is already filtered
    if (totalItems !== undefined) {
      return data;
    }

    return data.slice(startIndex, endIndex);
  }, [data, currentPage, pageSize, totalItems, effectiveShowPagination]);

  // Virtual row rendering (opt-in for large datasets)
  const shouldVirtualize = virtualize && paginatedData.length > 30;
  const rowVirtualizer = useVirtualizer({
    count: shouldVirtualize ? paginatedData.length : 0,
    getScrollElement: () => virtualScrollRef.current,
    estimateSize: () => virtualRowHeight + 8, // row height + spacer
    overscan: 5,
  });

  // Per-row selectability gate. Indices here are page-local (match the keys
  // stored in `selectedRows`). Default-true keeps every existing caller intact.
  const rowSelectable = (rowIndex: number): boolean =>
    !isRowSelectable || !!isRowSelectable(paginatedData[rowIndex], rowIndex);

  // Controlled-mode checked test for a single page row. Falls back to the
  // existing index-based test when uncontrolled, so callers without
  // `selectedRowIds` are byte-identical to before.
  const isRowChecked = (rowIndex: number): boolean => {
    if (isControlledSelection) {
      if (!getRowId) return false;
      return selectedIdSet.has(getRowId(paginatedData[rowIndex], rowIndex));
    }
    return selectedRows.has(rowIndex);
  };

  // Current-page selectable rows that the parent should hold as selected after
  // a row/header toggle. Only used in controlled mode — the parent reconciles
  // ONLY the current page from this report (off-page ids are never touched).
  const currentPageSelectableRows = () =>
    paginatedData.filter((_, index) => rowSelectable(index));

  // Handle row selection
  const handleRowSelect = (index: number, checked: boolean) => {
    if (!selectable) return;
    // Guard: a disabled row's checkbox must never toggle selection.
    if (!rowSelectable(index)) return;

    if (isControlledSelection) {
      // Parent owns selection. Report the resulting CURRENT-PAGE selected rows
      // (selectable page rows that are in the parent set, with this row toggled).
      if (onSelectionChange && getRowId) {
        const toggledId = getRowId(paginatedData[index], index);
        const selected = currentPageSelectableRows().filter((row, i) => {
          const id = getRowId(row, i);
          if (id === toggledId) return checked;
          return selectedIdSet.has(id);
        });
        onSelectionChange(selected);
      }
      return;
    }

    const newSelectedRows = new Set(selectedRows);
    if (checked) {
      newSelectedRows.add(index);
    } else {
      newSelectedRows.delete(index);
    }
    setSelectedRows(newSelectedRows);

    if (onSelectionChange) {
      const selected = Array.from(newSelectedRows)
        .map((idx) => {
          const actualIdx = effectiveShowPagination ? idx - (currentPage - 1) * pageSize : idx;
          return paginatedData[actualIdx];
        })
        .filter(Boolean);
      onSelectionChange(selected);
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (!selectable) return;

    if (isControlledSelection) {
      // Page-scoped: report all selectable rows on this page (or none). The
      // parent reconciles only the current page, so off-page ids are untouched.
      if (onSelectionChange) {
        onSelectionChange(checked ? currentPageSelectableRows() : []);
      }
      return;
    }

    const newSelectedRows = new Set<number>();
    if (checked) {
      paginatedData.forEach((_, index) => {
        if (rowSelectable(index)) newSelectedRows.add(index);
      });
    }
    setSelectedRows(newSelectedRows);

    if (onSelectionChange) {
      onSelectionChange(checked ? paginatedData.filter((_, index) => rowSelectable(index)) : []);
    }
  };

  // Get cell value
  const getCellValue = (column: Column<T>, row: T, index: number): React.ReactNode => {
    if (column.render) {
      return column.render(
        column.accessor
          ? typeof column.accessor === 'function'
            ? column.accessor(row)
            : row[column.accessor]
          : undefined,
        row,
        index
      );
    }

    if (typeof column.accessor === 'function') {
      return column.accessor(row);
    }

    if (column.accessor) {
      return row[column.accessor] ?? '';
    }

    return '';
  };

  // Select-all / indeterminate are computed over SELECTABLE rows only — a page
  // made entirely of non-selectable rows shows an unchecked, harmless header
  // checkbox; a mix shows checked once every selectable row is picked.
  const selectableIndices = paginatedData
    .map((_, index) => index)
    .filter((index) => rowSelectable(index));
  // In controlled mode "all/some" is computed over the current page's selectable
  // rows by id (∈ parent set); uncontrolled keeps the index-based test.
  const allSelected = selectableIndices.length > 0 && selectableIndices.every((index) => isRowChecked(index));
  const someSelected = selectableIndices.some((index) => isRowChecked(index)) && !allSelected;

  const bodyWrapperClasses = innerScroll
    ? 'overflow-x-auto max-h-[calc(100vh-300px)] bg-transparent overflow-y-auto scrollbar-hide sm:px-0'
    : 'overflow-x-auto bg-transparent scrollbar-hide sm:px-0';

  return (
    <div ref={tableContainerRef} className={cn('w-full scroll-mt-28', 'overflow-x-auto bg-transparent shadow-none', className)}>
      <div className="rounded-t-lg bg-transparent overflow-hidden w-full">
        <div ref={virtualScrollRef} className={bodyWrapperClasses}>
          <Table className="w-full border-0 min-w-max">
            <TableHeader className="sticky top-0 z-20 bg-gray-400">
              <TableRow className="border-none bg-gray-200 hover:bg-gray-200 data-[state=selected]:bg-gray-400">
                {selectable && (
                  <TableHead className="w-12 sticky left-0 z-30 border-0 bg-gray-200 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]">
                    <Checkbox
                      checked={allSelected || (someSelected ? "indeterminate" : false)}
                      onCheckedChange={(checked) => handleSelectAll(!!checked)}
                      className="bg-white"
                      disabled={effectivelyLoading}
                    />
                  </TableHead>
                )}
                {columns.map((column) => {
                  const isActionsColumn = column.key === 'actions';
                  return (
                    <TableHead
                      key={column.key}
                      className={cn(
                        'font-medium text-xs sm:text-sm text-[#08090A] border-none bg-gray-200',
                        column.headerClassName,
                        isActionsColumn && 'sticky right-0 z-30 bg-gray-200 text-right shadow-[-2px_0_4px_-2px_rgba(0,0,0,0.1)]'
                      )}
                    >
                      {column.header}
                    </TableHead>
                  );
                })}
              </TableRow>
            </TableHeader>
            <TableBody>
              {effectivelyLoading ? (
                Array.from({ length: pageSize || 10 }).map((_, rowIndex) => (
                  <React.Fragment key={`skeleton-row-${rowIndex}`}>
                    <TableRow className="border-0">
                      {selectable && (
                        <TableCell
                          className="sticky left-0 whitespace-nowrap z-10 py-2 sm:py-3 md:py-4 pl-2 sm:pl-3 md:pl-4 border-0 bg-white transition-colors group-hover:bg-[color-mix(in_srgb,hsl(var(--muted))_50%,white)] shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]"
                                                  >
                          <Skeleton className="h-4 w-4 rounded bg-white/50" />
                        </TableCell>
                      )}
                      {columns.map((column, colIdx) => {
                        const isActionsColumn = column.key === 'actions';
                        const isFirst = !selectable && colIdx === 0;
                        const isLast = colIdx === columns.length - 1;
                        return (
                          <TableCell
                            key={`skeleton-cell-${rowIndex}-${column.key}`}
                            className={cn(
                              'whitespace-nowrap py-2 sm:py-3 md:py-4 px-2 sm:px-3 md:px-4 border-0',
                              isActionsColumn && 'sticky right-0 z-10 bg-white text-right transition-colors group-hover:bg-[color-mix(in_srgb,hsl(var(--muted))_50%,white)] shadow-[-2px_0_4px_-2px_rgba(0,0,0,0.1)]',
                              isFirst && 'rounded-l-[12px]',
                              isLast && !isActionsColumn && 'rounded-r-[12px]'
                            )}
                                                      >
                            <Skeleton className={cn(
                              "h-4 rounded",
                              isActionsColumn ? "w-8 mx-auto" : "w-full"
                            )} />
                          </TableCell>
                        );
                      })}
                    </TableRow>
                    {rowIndex !== (pageSize || 10) - 1 && (
                      <TableRow key={`skeleton-spacer-${rowIndex}`} className="border-0">
                        <TableCell colSpan={columns.length + (selectable ? 1 : 0)} className="p-0 border-0">
                          <div style={{ height: 8 }}></div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))
              ) :
                paginatedData.length === 0 ? (
                  <TableRow className="border-0">
                    <TableCell
                      colSpan={columns.length + (selectable ? 1 : 0)}
                      className="h-24 text-center whitespace-nowrap text-muted-foreground"
                    >
                      {emptyMessage}
                    </TableCell>
                  </TableRow>
                ) : shouldVirtualize ? (
                  <>
                    {/* Virtual spacer: push rows down by the virtual offset */}
                    {rowVirtualizer.getVirtualItems().length > 0 && (
                      <tr style={{ height: rowVirtualizer.getVirtualItems()[0]?.start ?? 0 }}>
                        <td colSpan={columns.length + (selectable ? 1 : 0)} className="p-0 border-0" />
                      </tr>
                    )}
                    {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                      const rowIndex = virtualRow.index;
                      const row = paginatedData[rowIndex];
                      const actualIndex = showPagination ? (currentPage - 1) * pageSize + rowIndex : rowIndex;
                      const isSelected = isRowChecked(rowIndex);

                      return (
                        <React.Fragment key={`vrow-${actualIndex}`}>
                          <TableRow
                            className={cn('group border-0', onRowClick && 'cursor-pointer [&>td]:cursor-pointer')}
                            onClick={(e) => {
                              if ((e.target as HTMLElement).closest('[data-actions-cell]')) return
                              onRowClick?.(row, actualIndex)
                            }}
                          >
                            {selectable && (
                              <TableCell className="sticky left-0 whitespace-nowrap z-10 py-2 sm:py-3 md:py-4 pl-2 sm:pl-3 md:pl-4 border-0 bg-white transition-colors group-hover:bg-[color-mix(in_srgb,hsl(var(--muted))_50%,white)] shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]">
                                <Checkbox
                                  checked={isSelected}
                                  disabled={!rowSelectable(rowIndex)}
                                  onCheckedChange={(checked) => handleRowSelect(rowIndex, !!checked)}
                                  onClick={(e) => e.stopPropagation()}
                                  className="bg-white"
                                />
                              </TableCell>
                            )}
                            {columns.map((column, colIdx) => {
                              const isActionsColumn = column.key === 'actions';
                              const isFirst = !selectable && colIdx === 0;
                              const isLast = colIdx === columns.length - 1;
                              return (
                                <TableCell
                                  key={column.key}
                                  data-actions-cell={isActionsColumn || undefined}
                                  className={cn(
                                    'whitespace-nowrap py-2 sm:py-3 md:py-4 px-2 sm:px-3 md:px-4 border-0 text-xs sm:text-sm',
                                    column.className,
                                    isActionsColumn && 'sticky right-0 z-10 bg-white text-right transition-colors group-hover:bg-[color-mix(in_srgb,hsl(var(--muted))_50%,white)] shadow-[-2px_0_4px_-2px_rgba(0,0,0,0.1)]',
                                    isFirst && '',
                                    isLast && ''
                                  )}
                                >
                                  {getCellValue(column, row, actualIndex)}
                                </TableCell>
                              );
                            })}
                          </TableRow>
                          {rowIndex !== paginatedData.length - 1 && (
                            <TableRow className="border-0">
                              <TableCell colSpan={columns.length + (selectable ? 1 : 0)} className="p-0 border-0">
                                <div style={{ height: 8 }}></div>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                    {/* Virtual spacer: push bottom to maintain scroll height */}
                    {rowVirtualizer.getVirtualItems().length > 0 && (
                      <tr style={{ height: rowVirtualizer.getTotalSize() - (rowVirtualizer.getVirtualItems().at(-1)?.end ?? 0) }}>
                        <td colSpan={columns.length + (selectable ? 1 : 0)} className="p-0 border-0" />
                      </tr>
                    )}
                  </>
                ) : (
                  paginatedData.map((row, rowIndex) => {
                    const actualIndex = showPagination ? (currentPage - 1) * pageSize + rowIndex : rowIndex;
                    const isSelected = isRowChecked(rowIndex);

                    return (
                      <React.Fragment key={`row-fragment-${actualIndex}`}>
                        <TableRow
                          className={cn(
                            'group border-0',
                            onRowClick && 'cursor-pointer [&>td]:cursor-pointer'
                          )}
                          onClick={(e) => {
                            if ((e.target as HTMLElement).closest('[data-actions-cell]')) return
                            onRowClick?.(row, actualIndex)
                          }}
                        >
                          {selectable && (
                            <TableCell className={cn(
                              "sticky left-0 whitespace-nowrap z-10 py-2 sm:py-3 md:py-4 pl-2 sm:pl-3 md:pl-4 border-0 bg-white transition-colors group-hover:bg-[color-mix(in_srgb,hsl(var(--muted))_50%,white)] shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]",
                            )}>
                              <Checkbox
                                checked={isSelected}
                                disabled={!rowSelectable(rowIndex)}
                                onCheckedChange={(checked) => {
                                  handleRowSelect(rowIndex, !!checked);
                                }}
                                onClick={(e) => e.stopPropagation()}
                                className="bg-white"
                              />
                            </TableCell>
                          )}
                          {columns.map((column, colIdx) => {
                            const isActionsColumn = column.key === 'actions';
                            const isFirst = !selectable && colIdx === 0;
                            const isLast = colIdx === columns.length - 1;
                            return (
                              <TableCell
                                key={column.key}
                                data-actions-cell={isActionsColumn || undefined}
                                className={cn(
                                  'whitespace-nowrap py-2 sm:py-3 md:py-4 px-2 sm:px-3 md:px-4 border-0 text-xs sm:text-sm',
                                  column.className,
                                  isActionsColumn && 'sticky right-0 z-10 bg-white text-right transition-colors group-hover:bg-[color-mix(in_srgb,hsl(var(--muted))_50%,white)] shadow-[-2px_0_4px_-2px_rgba(0,0,0,0.1)]',
                                  isFirst && 'rounded-l-[12px]',
                                  isLast && !isActionsColumn && 'rounded-r-[12px]'
                                )}
                              >
                                {getCellValue(column, row, actualIndex)}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                        {rowIndex !== paginatedData.length - 1 && (
                          <TableRow key={`spacer-${actualIndex}`} className="border-0">
                            <TableCell colSpan={columns.length + (selectable ? 1 : 0)} className="p-0 border-0">
                              <div style={{ height: 8 }}></div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
            </TableBody>
          </Table>
        </div>
      </div>

      {effectiveShowPagination && (
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4 px-2 sm:px-4 py-3 sm:py-4 bg-[#fff] rounded-b-lg">
          {effectivelyLoading ? (
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 w-full">
              <div className="flex items-center gap-4">
                <Skeleton className="h-4 w-40" />
                <div className="flex items-center gap-2">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-8 w-16" />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Skeleton className="h-8 w-8" />
                <Skeleton className="h-8 w-8" />
                <div className="flex gap-1">
                  <Skeleton className="h-8 w-8" />
                  <Skeleton className="h-8 w-8" />
                  <Skeleton className="h-8 w-8" />
                </div>
                <Skeleton className="h-8 w-8" />
                <Skeleton className="h-8 w-8" />
              </div>
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs sm:text-sm text-muted-foreground w-full sm:w-auto">
                <span>
                  Showing {data.length > 0 ? (currentPage - 1) * pageSize + 1 : 0} to
                </span>
                {/* The page-size selector is inlined where the page-size number
                    sits in the sentence (no separate "Rows per page" block).
                    Shown whenever there's more than one valid size to pick. */}
                {pageSizeOptions.filter(size => size >= 10).length > 1 ? (
                  <Select
                    value={pageSize.toString()}
                    onValueChange={(value) => handlePageSizeChange(Number(value))}
                  >
                    <SelectTrigger className="h-6 w-[48px] pl-2 pr-1 text-xs sm:text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {pageSizeOptions.filter(size => size >= 10).map((size) => (
                        <SelectItem key={size} value={size.toString()}>
                          {size}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <span>{Math.min(currentPage * pageSize, totalItems ?? data.length)}</span>
                )}
                <span>of {totalItems ?? data.length} results</span>
              </div>
              {totalPages > 1 && (
                <div className="flex items-center gap-1 sm:gap-2 w-full sm:w-auto justify-end min-w-0 flex-wrap">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(1)}
                    disabled={currentPage === 1 || effectivelyLoading}
                    className="h-7 w-7 sm:h-8 sm:w-8 p-0 flex-shrink-0"
                  >
                    <ChevronsLeft className="h-3 w-3 sm:h-4 sm:w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1 || effectivelyLoading}
                    className="h-7 w-7 sm:h-8 sm:w-8 p-0 flex-shrink-0"
                  >

                    <ChevronLeft className="h-3 w-3 sm:h-4 sm:w-4" />
                  </Button>
                  <div className="flex items-center gap-0.5 sm:gap-1 min-w-0 flex-wrap">
                    {(() => {
                      const startPage = Math.max(1, currentPage - 1);
                      const endPage = Math.min(totalPages, currentPage + 1);
                      const showStartEllipsis = startPage > 1;
                      const showEndEllipsis = endPage < totalPages;
                      const pagesBefore = startPage - 1;
                      const pagesAfter = totalPages - endPage;
                      const showOnlyStartEllipsis = showStartEllipsis && showEndEllipsis && pagesBefore >= pagesAfter;
                      const showOnlyEndEllipsis = showStartEllipsis && showEndEllipsis && pagesAfter > pagesBefore;
                      const pages: (number | 'ellipsis')[] = [];
                      if (showStartEllipsis && !showOnlyEndEllipsis) {
                        pages.push(1);
                        pages.push('ellipsis');
                      }
                      for (let i = startPage; i <= endPage; i++) {
                        pages.push(i);
                      }
                      if (showEndEllipsis && !showOnlyStartEllipsis) {
                        pages.push('ellipsis');
                        pages.push(totalPages);
                      }
                      return pages.map((page, index) => {
                        if (page === 'ellipsis') {
                          return (
                            <div
                              key={`ellipsis-${index}`}
                              className="flex items-center justify-center h-7 w-7 sm:h-8 sm:w-8 flex-shrink-0"
                            >
                              <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
                            </div>
                          );
                        }
                        return (
                          <Button
                            key={page}
                            variant={currentPage === page ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => handlePageChange(page)}
                            disabled={effectivelyLoading}
                            className={cn(
                              'h-7 min-w-[2.25rem] sm:h-8 sm:min-w-[2.5rem] px-2 text-xs sm:text-sm flex-shrink-0',
                              currentPage === page && 'bg-black text-white hover:bg-black/90'
                            )}
                          >
                            {page}
                          </Button>
                        );
                      });
                    })()}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages || effectivelyLoading}
                    className="h-7 w-7 sm:h-8 sm:w-8 p-0 flex-shrink-0"
                  >
                    <ChevronRight className="h-3 w-3 sm:h-4 sm:w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(totalPages)}
                    disabled={currentPage === totalPages || effectivelyLoading}
                    className="h-7 w-7 sm:h-8 sm:w-8 p-0 flex-shrink-0"
                  >
                    <ChevronsRight className="h-3 w-3 sm:h-4 sm:w-4" />
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default DataTable;
